import { filter, sortBy } from 'ramda';
import type { EagColumnState } from '../../types/EagColumnState';
import { arraysShallowEqual } from '../../utils/arraysShallowEqual';
import type { EagGridState } from '../createEagGridStore';
import { useEagGridSelector } from '../useEagGridSelector';

// Factory that creates a cached column selector hook.
// Each call produces its own private WeakMap instance so selectors cannot
// collide on the same cache key even when multiple selectors share the same
// input array reference.
//
// The WeakMap is keyed on state.columnStates. useSyncExternalStore calls
// getSnapshot() 2-3 times per render and uses Object.is to verify consistency.
// Ramda filter/sortBy always return new arrays — without this cache, each call
// returns a different reference and React re-renders infinitely.
export function createCachedColumnSelector(predicate: (col: EagColumnState) => boolean): () => EagColumnState[] {
  const cache = new WeakMap<EagColumnState[], EagColumnState[]>();
  let lastResult: EagColumnState[] | null = null;
  const selector = (state: EagGridState): EagColumnState[] => {
    const cached = cache.get(state.columnStates);
    if (cached !== undefined) {
      return cached;
    }

    // Ramda filter for predicate, Ramda sortBy for ordering
    const result = sortBy((col: EagColumnState) => col.order, filter(predicate, state.columnStates));

    // Structural equality: if the filtered/sorted set hasn't changed
    // (same elements in same order), return the previous reference.
    // This prevents downstream re-renders when only width changed.
    // Works because setColumnWidth's .map() returns the original col
    // reference for columns whose key !== the resized column's key.
    if (lastResult !== null && arraysShallowEqual(lastResult, result)) {
      cache.set(state.columnStates, lastResult);
      return lastResult;
    }
    lastResult = result;
    cache.set(state.columnStates, result);
    return result;
  };
  return function useCachedColumnSelector(): EagColumnState[] {
    return useEagGridSelector(selector);
  };
}